import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { formatViews } from "@/utils/movie";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timerRef = useRef<number | null>(null);

  const goTo = useCallback(
    (index: number) => {
      if (items.length === 0) return;
      setActive((index + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return;
    timerRef.current = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden bg-background-100">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.heroImage ?? item.cover}
            alt={`${item.title} 焦点海报`}
            title={`${item.title} - 午夜免费视频精选`}
            className={`w-full h-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-background-50/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/40"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full mx-auto max-w-page px-4 md:px-8 flex items-center">
        <div key={items[active].id} className="w-full max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500/15 border border-primary-500/40 text-primary-300 text-xs font-label tracking-wider">
            <i className="ri-fire-fill"></i>
            本周午夜焦点
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-3xl md:text-6xl leading-tight text-foreground-950">
            {items[active].title}
          </h1>
          <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-xl">{items[active].tagline}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
            <span className="flex items-center gap-1 text-primary-400 font-semibold">
              <i className="ri-star-fill"></i>
              {items[active].rating.toFixed(1)}
            </span>
            <span>{items[active].year}</span>
            <span className="text-foreground-500">·</span>
            <span>{items[active].region}</span>
            <span className="text-foreground-500">·</span>
            <span>{items[active].genres.join(" / ")}</span>
            <span className="text-foreground-500">·</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(items[active].views)}
            </span>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[active].id}`}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-xl"></i>
              立即观看
            </Link>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-background-200/80 border border-background-300/70 text-foreground-950 font-medium hover:border-primary-500/60 transition-colors whitespace-nowrap"
            >
              <i className="ri-compass-3-line text-lg"></i>
              浏览全部影视
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute z-20 bottom-6 right-4 md:right-8 flex items-center gap-3">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(active - 1)}
          className="w-10 h-10 rounded-full bg-background-200/80 border border-background-300/70 text-foreground-950 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(active + 1)}
          className="w-10 h-10 rounded-full bg-background-200/80 border border-background-300/70 text-foreground-950 flex items-center justify-center hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl"></i>
        </button>
      </div>

      <div className="absolute z-20 bottom-7 left-1/2 -translate-x-1/2 md:left-8 md:translate-x-0 flex items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-8 bg-primary-500" : "w-3 bg-foreground-500/50 hover:bg-foreground-500"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}